import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { CheckCircle2, Loader2 } from 'lucide-react';

const ConfirmEmail: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams]);
  const email = useMemo(() => searchParams.get('email')?.trim() ?? '', [searchParams]);
  const linkInvalid = !token || !email;

  const [isLoading, setIsLoading] = useState(!linkInvalid);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (linkInvalid) {
      setIsLoading(false);
      setError(t('invalidConfirmEmailLink'));
      return;
    }

    let isMounted = true;

    const run = async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_SIGNER_API_URL}/api/User/ConfirmEmail?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;
        const response = await fetch(apiUrl, { method: 'GET' });
        const text = await response.text();
        let data: Record<string, unknown> = {};
        if (text.trim()) {
          try {
            data = JSON.parse(text) as Record<string, unknown>;
          } catch {
            data = { message: text.trim() };
          }
        }

        if (!response.ok) {
          const message =
            (typeof data.message === 'string' && data.message.trim()) ||
            (typeof data.detail === 'string' && data.detail.trim()) ||
            (typeof data.title === 'string' && data.title.trim()) ||
            t('confirmEmailFailed');
          throw new Error(message);
        }

        const message =
          (typeof data.message === 'string' && data.message.trim()) || t('confirmEmailSuccess');

        if (!isMounted) {
          return;
        }

        setSuccessMessage(message);
        window.setTimeout(() => {
          navigate('/login', { replace: true });
        }, 1800);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err.message : t('confirmEmailFailed'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [email, linkInvalid, navigate, t, token]);

  return (
    <div className="mx-auto mt-10 w-full max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-3xl">{t('confirmEmailTitle')}</CardTitle>
          <CardDescription className="text-center">
            {isLoading ? t('confirmingEmail') : successMessage ? t('confirmEmailSuccessHint') : t('confirmEmailFailedHint')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <Alert>
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('confirmingEmail')}</span>
              </div>
            </Alert>
          )}

          {!isLoading && successMessage && (
            <>
              <Alert variant="success">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4" />
                  <span>{successMessage}</span>
                </div>
              </Alert>
              <Button className="w-full" asChild>
                <RouterLink to="/login">{t('backToSignIn')}</RouterLink>
              </Button>
            </>
          )}

          {!isLoading && error && (
            <>
              <Alert variant="destructive">{error}</Alert>
              <Button variant="outline" className="w-full" asChild>
                <RouterLink to="/login">{t('backToSignIn')}</RouterLink>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmEmail;
